import Color from 'color';

import * as Run from './runs';
import type * as RunTypes from './runTypes';
import type {RunColorConfig} from './section';

/**
 * Run color group keys persist colors for grouped run rows and support
 * inheritance through nested group paths.
 *
 * The serialized key is composed as:
 * run-color-group:<encoded runset id>:<encoded group path>
 *
 * `encoded runset id` is the runset id encoded as a URI component.
 *
 * `encoded group path` is the group path encoded as a URI component, where the
 * nested path can look like:
 *   Example:
 *   path = [
 *     {kind: "group", key: "run:group", value: "sweep-beta"},
 *     {kind: "group", key: "config:domain", value: "tabular"}
 *   ]
 */

export const RUN_COLOR_GROUP_KEY_PREFIX = 'run-color-group:';

/**
 * Saved custom run colors can hold strings `color` cannot parse (e.g. a
 * truncated `rgb(23`). Treat those as unset so they fall back to the default
 * color instead of throwing during render.
 */
export function isUsableRunColor(
  color: string | null | undefined
): color is string {
  if (!color?.trim()) {
    return false;
  }
  try {
    Color(color);
    return true;
  } catch {
    return false;
  }
}

export type RunColorGroupPathEntry = {
  kind: 'group';
  key: string;
  value: string | null;
};

export type ParsedRunColorGroupKey = {
  runsetId: string | null;
  path: RunColorGroupPathEntry[];
};

export type ParsedRunColorGroupKeyWithSource = ParsedRunColorGroupKey & {
  key: string;
};

type RunColorGroupColorMatch = {
  color: string;
  parentInheritedColor?: string;
  source: 'explicit' | 'inherited';
};

function isRunColorGroupPathEntry(
  value: unknown
): value is RunColorGroupPathEntry {
  if (value == null || typeof value !== 'object') {
    return false;
  }

  const entry = value as RunColorGroupPathEntry;
  return (
    entry.kind === 'group' &&
    typeof entry.key === 'string' &&
    (typeof entry.value === 'string' || entry.value == null)
  );
}

/**
 * Serialized run color group key used for persistence and retrieval.
 */
export function buildRunColorGroupKey(
  runsetId: string | null | undefined,
  path: RunColorGroupPathEntry[]
) {
  return `${RUN_COLOR_GROUP_KEY_PREFIX}${encodeURIComponent(
    JSON.stringify(runsetId ?? null)
  )}:${encodeURIComponent(JSON.stringify(path))}`;
}

/**
 * Takes in a run color key that is either a non-group key or a group color key.
 * Non-group run color keys do not support inheritance, so they return null.
 */
export function parseRunColorGroupKey(
  key: string
): ParsedRunColorGroupKey | null {
  if (!key.startsWith(RUN_COLOR_GROUP_KEY_PREFIX)) {
    return null;
  }

  const encodedParts = key.slice(RUN_COLOR_GROUP_KEY_PREFIX.length);
  const separatorIndex = encodedParts.indexOf(':');
  if (separatorIndex === -1) {
    return null;
  }

  try {
    const runsetId = JSON.parse(
      decodeURIComponent(encodedParts.slice(0, separatorIndex))
    );
    const path = JSON.parse(
      decodeURIComponent(encodedParts.slice(separatorIndex + 1))
    );
    if (
      !(typeof runsetId === 'string' || runsetId == null) ||
      !Array.isArray(path) ||
      !path.every(isRunColorGroupPathEntry)
    ) {
      return null;
    }

    return {runsetId: runsetId ?? null, path};
  } catch {
    return null;
  }
}

/**
 * Given a run and an ordered list of group keys by nested depth, return
 * a list of path entries for the group path in the same nested depth order.
 */
export function buildRunColorGroupPath(
  run: Run.RunWithUniqueId,
  groupKeys: RunTypes.Key[]
): RunColorGroupPathEntry[] {
  const normalizeRunColorGroupValue = (value: RunTypes.Value) =>
    value == null ? null : String(value);

  return groupKeys.map(groupKey => ({
    kind: 'group' as const,
    key: Run.keyToString(groupKey),
    value: normalizeRunColorGroupValue(Run.getValue(run, groupKey)),
  }));
}

export function getRunColorGroupKey(
  run: Run.RunWithUniqueId,
  groupKeys: RunTypes.Key[]
): string {
  return buildRunColorGroupKey(
    run.runsetInfo?.id,
    buildRunColorGroupPath(run, groupKeys)
  );
}

export function getRunColorGroupKeyForDepth(
  run: Run.RunWithUniqueId,
  groupKeys: RunTypes.Key[],
  recursionDepth: number
) {
  return buildRunColorGroupKey(
    run.runsetInfo?.id,
    buildRunColorGroupPath(run, groupKeys.slice(0, recursionDepth + 1))
  );
}

/**
 * Given a runset id and a path, return a list of ancestor keys
 * for lookup (typically backwards for ancestor lookup)
 */
export function getRunColorGroupAncestorKeys(
  runsetId: string | null | undefined,
  path: RunColorGroupPathEntry[]
) {
  return path.map((_entry, i) =>
    buildRunColorGroupKey(runsetId, path.slice(0, i + 1))
  );
}

export function getRunColorGroupResolvedColor(
  customRunColors: RunColorConfig | undefined,
  run: Run.RunWithUniqueId,
  groupKeys: RunTypes.Key[]
): RunColorGroupColorMatch | undefined {
  if (customRunColors == null || groupKeys.length === 0) {
    return undefined;
  }

  const path = buildRunColorGroupPath(run, groupKeys);
  const ancestorKeys = getRunColorGroupAncestorKeys(run.runsetInfo?.id, path);
  let parentInheritedColor: string | undefined;

  // start at the second to last key and work backwards to find the parent inherited color
  // last key being the current run color key
  for (let i = ancestorKeys.length - 2; i >= 0; i--) {
    const color = customRunColors[ancestorKeys[i]];
    if (isUsableRunColor(color)) {
      parentInheritedColor = color;
      break;
    }
  }

  const explicitColor = customRunColors[ancestorKeys[ancestorKeys.length - 1]];
  if (isUsableRunColor(explicitColor)) {
    return {
      color: explicitColor,
      parentInheritedColor,
      source: 'explicit',
    };
  }

  if (parentInheritedColor != null) {
    return {
      color: parentInheritedColor,
      parentInheritedColor,
      source: 'inherited',
    };
  }

  return undefined;
}

export function getRunColorGroupDescendantKeys(
  customRunColors: RunColorConfig | undefined,
  parentKey: string
) {
  return getRunColorGroupDescendantKeysFromParsedKeys(
    getParsedRunColorGroupKeys(customRunColors),
    parentKey
  );
}

export function getParsedRunColorGroupKeys(
  customRunColors: RunColorConfig | undefined
) {
  if (customRunColors == null) {
    return [];
  }

  return Object.keys(customRunColors)
    .flatMap(key => {
      const parsed = parseRunColorGroupKey(key);
      return parsed == null ? [] : [{...parsed, key}];
    })
    .sort(compareParsedRunColorGroupKeys);
}

function getRunColorGroupPathSortKey(path: RunColorGroupPathEntry[]) {
  return JSON.stringify(
    path.map(entry => [entry.kind, entry.key, entry.value])
  );
}

function compareParsedRunColorGroupKeys(
  a: ParsedRunColorGroupKeyWithSource,
  b: ParsedRunColorGroupKeyWithSource
) {
  const pathCompare = getRunColorGroupPathSortKey(a.path).localeCompare(
    getRunColorGroupPathSortKey(b.path)
  );
  if (pathCompare !== 0) {
    return pathCompare;
  }

  return a.key.localeCompare(b.key);
}

function pathsMatchPrefix(
  path: RunColorGroupPathEntry[],
  prefix: RunColorGroupPathEntry[]
) {
  if (prefix.length > path.length) {
    return false;
  }

  return prefix.every((prefixEntry, i) => {
    const entry = path[i];
    return (
      entry.kind === prefixEntry.kind &&
      entry.key === prefixEntry.key &&
      entry.value === prefixEntry.value
    );
  });
}

/**
 * Given a parent key and a list of parsed keys that should be
 * derived from customRunColors, return saved group color keys
 * that live underneath it.
 */
export function getRunColorGroupDescendantKeysFromParsedKeys(
  parsedKeys: ParsedRunColorGroupKeyWithSource[],
  parentKey: string
) {
  const parent = parseRunColorGroupKey(parentKey);
  if (parent == null) {
    return [];
  }

  const descendants = parsedKeys
    .filter(
      parsed =>
        parsed.runsetId === parent.runsetId &&
        parsed.path.length > parent.path.length &&
        pathsMatchPrefix(parsed.path, parent.path)
    )
    .sort(compareParsedRunColorGroupKeys);

  const immediateChildDepth = parent.path.length + 1;
  const immediateChildren = descendants.filter(
    descendant => descendant.path.length === immediateChildDepth
  );
  const nestedChildren = descendants.filter(
    descendant => descendant.path.length > immediateChildDepth
  );

  return [...immediateChildren, ...nestedChildren].map(
    descendant => descendant.key
  );
}
